import { exitWeekToLifeSpan, lifeSpanToExitWeek } from '@bestseller/retail-planning.styletimeline';
import { useCallback, useMemo, useRef } from 'react';
import { Cell } from 'src/components/organisms/DataTable/types';
import { Module } from 'src/domain';
import { StyleTimelineOverviewRow } from 'src/domain/table/style-timeline-overview-row';
import { calculateSuggestedNormClosureWeek } from 'src/utils/normUtils';
import { useUnsavedChangesModule } from '../unsaved-changes';

type ExitWeekType = 'EMPTY' | 'INFINITE' | 'WEEK_KEY';

function getLifeSpanFromCellValue(
    value: Pick<StyleTimelineOverviewRow, 'lifeSpan' | 'exitWeekType'> | number
): number | null {
    if (typeof value === 'number') {
        return value;
    }
    if (value && typeof value === 'object' && 'lifeSpan' in value) {
        return typeof value.lifeSpan === 'number' ? value.lifeSpan : null;
    }
    return null;
}

function getExitWeekFromCellValue(
    value: Pick<StyleTimelineOverviewRow, 'exitWeek' | 'exitWeekType'> | number
): number | null {
    if (typeof value === 'number') {
        return value;
    }
    if (value && typeof value === 'object' && 'exitWeek' in value) {
        return typeof value.exitWeek === 'number' ? value.exitWeek : null;
    }
    return null;
}

function getExitWeekTypeFromCellValue(value: Pick<StyleTimelineOverviewRow, 'exitWeekType'> | number): ExitWeekType {
    if (value && typeof value === 'object' && 'exitWeekType' in value) {
        return value.exitWeekType as ExitWeekType;
    }

    if (typeof value === 'number') {
        return 'WEEK_KEY';
    }

    return 'EMPTY';
}

export const useStyleTimelineHandleCellValueChanges = (simulationData: StyleTimelineOverviewRow[]) => {
    const [unsavedChanges, setUnsavedChanges] = useUnsavedChangesModule<StyleTimelineOverviewRow[]>(
        Module.StyleTimeline
    );
    const unsavedChangesRef = useRef(unsavedChanges);
    unsavedChangesRef.current = unsavedChanges;

    const simulationMap = useMemo(() => new Map(simulationData.map((r) => [r.rowId, r])), [simulationData]);

    const callback = useCallback(
        (cells: Cell<unknown>[]) => {
            const newUnsavedChanges = [...(unsavedChangesRef.current ?? [])];
            const unsavedMap = new Map(newUnsavedChanges.map((r, i) => [r.rowId, i]));

            cells.forEach((cell) => {
                // skip row group cells
                if (cell.rowId.toString().startsWith('row-group')) {
                    return;
                }

                if (cell.column) {
                    const simulationRow = simulationMap.get(cell.rowId);
                    if (!simulationRow) {
                        return;
                    }

                    let newData = {
                        [cell.column]: cell.value,
                    };

                    if (cell.column === 'packaway') {
                        const [packawayStartWeek, packawayEndWeek] =
                            (cell.value as string | undefined)?.split('-') ?? [];

                        newData = {
                            packawayStartWeek: packawayStartWeek ? Number(packawayStartWeek) : null,
                            packawayEndWeek: packawayEndWeek ? Number(packawayEndWeek) : null,
                        };
                    }

                    if (cell.column === 'eligibleForSale') {
                        newData[cell.column] = Boolean(cell.value);
                    }

                    const startWeek = simulationRow.startWeek;

                    if (isColumn<Pick<StyleTimelineOverviewRow, 'normClosureWeek'>>(cell, 'normClosureWeek')) {
                        newData.normClosureWeek =
                            typeof cell.value === 'number' && cell.value !== 0 ? cell.value : null;
                    }

                    if (
                        isColumn<Pick<StyleTimelineOverviewRow, 'exitWeek' | 'exitWeekType' | 'lifeSpan'>>(
                            cell,
                            'exitWeek'
                        )
                    ) {
                        const exitWeek = getExitWeekFromCellValue(cell.value);
                        const exitWeekType = getExitWeekTypeFromCellValue(cell.value);
                        const lifeSpan = typeof exitWeek === 'number' ? exitWeekToLifeSpan(startWeek, exitWeek) : null;
                        const suggestedNormClosureWeek = calculateSuggestedNormClosureWeek(exitWeek);

                        newData = {
                            ...newData,
                            lifeSpan: lifeSpan,
                            exitWeek: exitWeek,
                            exitWeekType,
                            normClosureWeekExpected: suggestedNormClosureWeek,
                        };
                    }

                    if (
                        isColumn<Pick<StyleTimelineOverviewRow, 'lifeSpan' | 'exitWeekType' | 'exitWeek'>>(
                            cell,
                            'lifeSpan'
                        )
                    ) {
                        const lifeSpan = getLifeSpanFromCellValue(cell.value);
                        const exitWeekType = getExitWeekTypeFromCellValue(cell.value);
                        const exitWeek = typeof lifeSpan === 'number' ? lifeSpanToExitWeek(startWeek, lifeSpan) : null;

                        newData = {
                            ...newData,
                            lifeSpan: lifeSpan,
                            exitWeekType,
                            exitWeek,
                        };
                    }

                    const rowIndexToUpdate = unsavedMap.get(cell.rowId) ?? -1;
                    if (rowIndexToUpdate >= 0) {
                        newUnsavedChanges[rowIndexToUpdate] = {
                            ...newUnsavedChanges[rowIndexToUpdate],
                            ...newData,
                        };
                    } else {
                        newUnsavedChanges.push({
                            ...simulationRow,
                            ...newData,
                        });
                        unsavedMap.set(cell.rowId, newUnsavedChanges.length - 1);
                    }
                }
            });

            setUnsavedChanges(newUnsavedChanges);
        },
        [setUnsavedChanges, simulationMap]
    );

    return callback;
};

// biome-ignore lint/suspicious/noExplicitAny: we need to use any
function isColumn<CellType>(cell: any, columnName: string): cell is Cell<CellType> {
    return cell.column === columnName;
}
